import { faClose } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, {
  ComponentProps,
  ComponentPropsWithoutRef,
  FC,
  ReactNode,
  SyntheticEvent,
  useContext,
  useState,
} from 'react'
import { SWRResponse } from 'swr'
import {
  BuyModal,
  BuyStep,
  useDynamicTokens,
} from '@reservoir0x/reservoir-kit-ui'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { CSS } from '@stitches/react'

import { ReferralContext } from '../../context/ReferralContextProvider'
import { BuyTokenBodyParameters } from '@reservoir0x/reservoir-sdk'
import { useAuthLogin } from 'hooks/auth/useAuthLogin'
import chains, { ReservoirChain } from 'utils/chains'
import { ETHChains } from 'utils/chains'

import { Button, Flex, Text } from 'components/primitives'
import { Dialog } from 'components/primitives/Dialog'
import { AnimatePresence } from 'framer-motion'

import {
  ClickAnalyticsProps,
  withClickAnalytics,
} from 'components/wrappers/withClickAnalytics'
import { PrivyLoginWrapper } from 'components/custom/PrivyLoginWrapper'
import CrossmintPayButtonWrapper from './CrossmintPayButtonWrapper'
import { CROSSMINT_USD_LIMIT, getCrossmintConfig } from 'lib/crossmint'
import { analytics } from 'utils/helpers/analytics'
import { useAuthWallets } from 'hooks/auth/useAuthWallets'
import InfoSubtitle from 'components/custom/InfoSubtitle'
import { useAccountPrivyWallet } from 'hooks/auth/useAccountPrivyWallet'
import { getReservoirChainFromContract } from 'config/routes'

type Props = {
  tokenId?: string
  contract?: string
  orderId?: string
  executionMethod?: BuyTokenBodyParameters['executionMethod']
  buttonCss?: CSS
  buttonProps?: ComponentProps<typeof Button>
  buttonChildren?: ReactNode
  mutate?: SWRResponse['mutate']
  openState?: ComponentPropsWithoutRef<typeof BuyModal>['openState']
  source?: string
  quantity?: number
  token: ReturnType<typeof useDynamicTokens>['data'][0]
  setIsBuyNowOpen?: React.Dispatch<React.SetStateAction<boolean>>
  disabled?: boolean
}

const BuyNow: FC<Props & ClickAnalyticsProps> = withClickAnalytics(
  ({
    tokenId,
    contract,
    disabled = false,
    orderId = undefined,
    executionMethod,
    mutate,
    buttonCss,
    buttonProps = {},
    buttonChildren,
    openState,
    source = '',
    token,
    setIsBuyNowOpen = () => {},
    quantity = 1,
  }) => {
    const { login } = useAuthLogin()
    const { feesOnTop } = useContext(ReferralContext)
    const [open, setOpen] = useState<boolean>(false)
    const { changeAccountActiveWallet } = useAccountPrivyWallet()
    const { ownershipWallet } = useAuthWallets()
    const tokenChain = contract ? getReservoirChainFromContract(contract) : null

    const isPopChain = !ETHChains.includes(tokenChain?.id ?? 0)
    const crossmintConfig = getCrossmintConfig(
      tokenChain?.id ?? 0,
      ownershipWallet?.address,
      contract
    )

    const trigger = (
      <Button
        analyticsId="click marketplace buy with crypto"
        css={buttonCss}
        color="neutralPrimary"
        {...buttonProps}
        corners="rounded"
        size="large"
      >
        <img
          src="/icons/eth-icon-light.svg"
          alt="Ethereum logo"
          width={10}
          height={10}
        />
        <Text css={{ fontWeight: 600, fontSize: 16 }}>Buy with ETH</Text>
      </Button>
    )

    const isOverCrossmintPurchaseLimit =
      (token?.market?.floorAsk?.price?.amount?.usd ?? 0) >= CROSSMINT_USD_LIMIT

    const handleCrossmintClick = (e: SyntheticEvent) => {
      if (ownershipWallet != null && crossmintConfig != null) {
        analytics
          .trackWebEvent('click marketplace buy with credit card')
          .catch(console.error)
        return
      } else {
        console.error(
          `No ${
            ownershipWallet ? 'crossmint config' : 'ownership wallet'
          } found`
        )
        setOpen(false)
        analytics
          .trackWebEvent('connect wallet', {
            where: 'click marketplace buy with credit card',
          })
          .catch(console.error)
        e.preventDefault()
      }
    }

    const crossmintTrigger = (
      <CrossmintPayButtonWrapper
        checkoutProps={{
          experimental: true,
          display: 'new-tab',
          delivery: 'non-custodial',
          paymentMethods: ['fiat'],
        }}
        clientId={crossmintConfig?.clientId ?? ''}
        mintConfig={{
          quantity,
          contractAddress: contract,
          tokenId: tokenId,
        }}
        disabled={isOverCrossmintPurchaseLimit}
        mintTo={ownershipWallet?.address}
        getButtonText={() => 'Buy with Credit Card'}
        onClick={handleCrossmintClick}
      />
    )

    return (
      <>
        <Dialog
          open={open}
          onOpenChange={(open) => {
            if (!open) {
              setIsBuyNowOpen(false)
              setOpen(false)
            }
            setIsBuyNowOpen(open)
          }}
          overlayProps={{ style: { opacity: 0.7 } }}
          style={{ zIndex: 500 }}
        >
          <AnimatePresence>
            <Flex
              css={{
                px: 25,
                py: 16,
                backgroundColor: '$gray3',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Text style="subtitle1">Select Payment Option</Text>
              <DialogPrimitive.Close asChild>
                <Button
                  color="ghost"
                  size="none"
                  css={{ color: '$neutralText' }}
                >
                  <FontAwesomeIcon icon={faClose} width={16} height={16} />
                </Button>
              </DialogPrimitive.Close>
            </Flex>
            <Flex
              direction="column"
              css={{ p: 24, gap: '$4', background: '$gray2' }}
              align="center"
            >
              <Flex
                css={{ gap: '$2', mt: '$2', width: '100%' }}
                direction="column"
                justify="end"
              >
                <PrivyLoginWrapper trigger={crossmintTrigger}>
                  {crossmintTrigger}
                </PrivyLoginWrapper>

                {isOverCrossmintPurchaseLimit && (
                  <InfoSubtitle
                    infoText={`Credit card payments are not supported on purchases exceeding $${CROSSMINT_USD_LIMIT}.`}
                  />
                )}
              </Flex>
              <Flex
                css={{ gap: '$2', mt: '$2' }}
                direction="column"
                justify="end"
              >
                <PrivyLoginWrapper trigger={trigger}>
                  <BuyModal
                    trigger={trigger}
                    token={`${contract}:${tokenId}`}
                    orderId={orderId}
                    openState={openState}
                    //executeBuyOptions?: Omit<{
                    //  executionMethod?: "seaport-intent" | "intent" | undefined;
                    executeBuyOptions={{ executionMethod, partial: true }}
                    onConnectWallet={() => {
                      login?.()
                    }}
                    // CONFIGURABLE: set any fees on top of orders, note that these will only
                    // apply to native orders (using the reservoir order book) and not to external orders (opensea, blur etc)
                    // Refer to our docs for more info: https://docs.reservoir.tools/reference/sweepmodal-1
                    // feesOnTopBps={["0xabc:50"]}
                    feesOnTopUsd={feesOnTop}
                    chainId={tokenChain?.id}
                    onClose={(data, stepData, currentStep) => {
                      setIsBuyNowOpen(false)
                      if (mutate && currentStep == BuyStep.Complete) mutate()
                    }}
                    defaultQuantity={quantity}
                  />
                </PrivyLoginWrapper>
                {isPopChain && (
                  <Text
                    style="body2"
                    css={{
                      textAlign: 'center',
                      color: '$errorRed',
                      py: '$1',
                      px: '$1',
                      '@md': {
                        px: 0,
                      },
                      wordBreak: 'break-word',
                    }}
                  >
                    <strong>Note:</strong> This purchase will be untradable
                    until you reach Command Rank 10 in game or apply a Trade
                    License.
                    <Text
                      style="body2"
                      css={{
                        '&:hover': {
                          color: '$primary10',
                        },
                      }}
                    >
                      <a
                        href="https://docs.piratenation.game/learn/trade-licenses"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {' '}
                        Learn More.
                      </a>
                    </Text>
                    <br />
                    <br />
                    Make sure you are connected to your gameplay wallet before
                    purchase.
                  </Text>
                )}
              </Flex>
            </Flex>
          </AnimatePresence>
        </Dialog>
        <Button
          analyticsId="click marketplace buy now"
          css={buttonCss}
          color="primary"
          onClick={() => {
            setOpen(true)
            changeAccountActiveWallet()
          }}
          {...buttonProps}
          disabled={disabled}
        >
          {buttonChildren}
        </Button>
      </>
    )
  }
)

export default BuyNow
